/**
 * <s-product-gallery> — Main product photo with a swipeable row of square thumbnails.
 *
 * Contract (inside the element):
 *   [data-gallery-track]          — Horizontal scroll-snap track; each direct child is one full-width slide.
 *   [data-media-id] on each slide — Shopify media id of the slide.
 *   [data-gallery-thumb]          — Thumbnail buttons, same order as the slides; data-index = slide index.
 *   [data-gallery-thumbs]         — Scrollable thumbnail strip.
 *   script[data-variant-media]    — JSON map { variantId: mediaId } for colour → photo syncing.
 *
 * Variant syncing: listens for option `change` events inside the closest [data-product-root]
 * (the product page grid) and shows the selected variant's photo.
 */
class SProductGallery extends HTMLElement {
  #track = null;
  #thumbs = [];
  #strip = null;
  #variantMedia = {};
  #observer = null;
  #root = null;
  #activeIndex = 0;
  /** Colour key per slide (same order as the slides); null = photo shown for every colour. */
  #slideColours = [];
  #thumbItems = [];
  #currentColour = null;

  static #VIEW_WORDS = new Set([
    'front', 'back', 'left', 'right', 'side', 'zoomed', 'in', 'flat', 'lifestyle', 'closeup',
    'close', 'up', 'detail', 'details', 'top', 'bottom', 'folded', 'hanging', 'model', 'mockup',
    'product', 'default', 'inside', 'outside', 'label',
  ]);

  /** Printful file names look like "unisex-classic-tee-military-green-left-front-6ae8b94f.jpg". */
  static #fileTokens(file) {
    return (file || '')
      .toLowerCase()
      .replace(/\.[a-z0-9]+$/, '')
      .replace(/_[0-9a-f]{6,}$/, '')
      .replace(/-[0-9a-f]{8,}$/, '')
      .split('-')
      .filter(Boolean);
  }

  /**
   * Groups the photos by colour using the file names, with each variant's own photo as the
   * reference for its colour. Leaves filtering off when fewer than two colours are found.
   */
  #buildColours() {
    const slides = this.#slides();
    const tokens = slides.map((slide) => SProductGallery.#fileTokens(slide.dataset.mediaFile));
    const named = tokens.filter((t) => t.length > 1);
    if (named.length < 2) return;

    // Shared product prefix, e.g. ["unisex", "classic", "tee"].
    let prefix = named[0].length - 1;
    for (const t of named) {
      let i = 0;
      while (i < prefix && i < t.length - 1 && t[i] === named[0][i]) i++;
      prefix = i;
    }
    const rests = tokens.map((t) => t.slice(prefix).join('-'));

    // Colour keys from each variant's own photo, with view words stripped from the end.
    const keys = new Set();
    for (const mediaId of Object.values(this.#variantMedia)) {
      const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
      if (index < 0) continue;
      const parts = tokens[index].slice(prefix);
      while (parts.length && (SProductGallery.#VIEW_WORDS.has(parts.at(-1)) || /^\d+$/.test(parts.at(-1)))) parts.pop();
      if (parts.length) keys.add(parts.join('-'));
    }
    if (keys.size < 2) return;

    const sortedKeys = [...keys].sort((a, b) => b.length - a.length);
    this.#slideColours = rests.map(
      (rest) => sortedKeys.find((key) => rest === key || rest.startsWith(`${key}-`)) || null,
    );
  }

  /** Shows only the photos of the variant's colour (plus photos without a colour). */
  #filterFor(variantId) {
    if (!this.#slideColours.length) return;
    const mediaId = this.#variantMedia[variantId];
    const slides = this.#slides();
    const featured = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    const colour = featured >= 0 ? this.#slideColours[featured] : null;
    if (!colour || colour === this.#currentColour) return;
    this.#currentColour = colour;

    slides.forEach((slide, i) => {
      const show = !this.#slideColours[i] || this.#slideColours[i] === colour;
      slide.style.display = show ? '' : 'none';
      if (this.#thumbItems[i]) this.#thumbItems[i].style.display = show ? '' : 'none';
    });
    if (this.#strip) this.#strip.scrollLeft = 0;
    this.#updateArrows();
  }

  #prev = null;
  #next = null;

  #onArrowClick = (event) => {
    if (!this.#strip) return;
    const direction = event.currentTarget === this.#prev ? -1 : 1;
    this.#strip.scrollBy({ left: direction * this.#strip.clientWidth * 0.8, behavior: this.#behavior(false) });
  };

  #updateArrows = () => {
    if (!this.#strip) return;
    const max = this.#strip.scrollWidth - this.#strip.clientWidth;
    const atStart = this.#strip.scrollLeft <= 2;
    const atEnd = this.#strip.scrollLeft >= max - 2;
    if (this.#prev) this.#prev.disabled = atStart;
    if (this.#next) this.#next.disabled = atEnd;
    this.toggleAttribute('data-thumbs-overflow', max > 2);
  };

  #onThumbClick = (event) => {
    const thumb = event.target.closest('[data-gallery-thumb]');
    if (!thumb) return;
    this.goTo(Number(thumb.dataset.index));
  };

  #onVariantChange = (event) => {
    const input = event.target;
    const variantId = input?.dataset?.variantId || input?.selectedOptions?.[0]?.dataset?.variantId;
    if (!variantId) return;
    this.#showVariant(variantId);
  };

  connectedCallback() {
    this.#track = this.querySelector('[data-gallery-track]');
    this.#strip = this.querySelector('[data-gallery-thumbs]');
    this.#thumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
    if (!this.#track) return;

    try {
      this.#variantMedia = JSON.parse(this.querySelector('script[data-variant-media]')?.textContent || '{}');
    } catch {
      this.#variantMedia = {};
    }

    this.#strip?.addEventListener('click', this.#onThumbClick);
    this.#prev = this.querySelector('[data-thumbs-prev]');
    this.#next = this.querySelector('[data-thumbs-next]');
    this.#prev?.addEventListener('click', this.#onArrowClick);
    this.#next?.addEventListener('click', this.#onArrowClick);
    this.#strip?.addEventListener('scroll', this.#updateArrows, { passive: true });
    window.addEventListener('resize', this.#updateArrows, { passive: true });
    this.#updateArrows();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) this.#setActive(this.#slides().indexOf(entry.target));
        }
      },
      { root: this.#track, threshold: 0.6 },
    );
    this.#slides().forEach((slide) => this.#observer.observe(slide));

    this.#root = this.closest('[data-product-root]');
    this.#root?.addEventListener('change', this.#onVariantChange);

    // Deep links (?variant=) open on that variant's photo.
    this.#thumbItems = this.#thumbs.map((thumb) => thumb.closest('[data-gallery-thumb-item]'));
    this.#buildColours();

    if (this.dataset.initialVariant) this.#showVariant(this.dataset.initialVariant, true);
    else if (this.dataset.filterVariant) {
      this.#filterFor(this.dataset.filterVariant);
      const first = this.#slides().findIndex((slide) => slide.style.display !== 'none');
      if (first > 0) this.goTo(first, true);
    }
  }

  disconnectedCallback() {
    this.#strip?.removeEventListener('click', this.#onThumbClick);
    this.#strip?.removeEventListener('scroll', this.#updateArrows);
    this.#prev?.removeEventListener('click', this.#onArrowClick);
    this.#next?.removeEventListener('click', this.#onArrowClick);
    window.removeEventListener('resize', this.#updateArrows);
    this.#root?.removeEventListener('change', this.#onVariantChange);
    this.#observer?.disconnect();
  }

  #slides() {
    return Array.from(this.#track.children);
  }

  #behavior(instant) {
    if (instant) return 'instant';
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
  }

  goTo(index, instant = false) {
    const slide = this.#slides()[index];
    if (!slide) return;
    this.#track.scrollTo({ left: slide.offsetLeft - this.#track.offsetLeft, behavior: this.#behavior(instant) });
    this.#setActive(index, instant);
  }

  #showVariant(variantId, instant = false) {
    const mediaId = this.#variantMedia[variantId];
    if (!mediaId) return;
    this.#filterFor(variantId);
    const index = this.#slides().findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0 && index !== this.#activeIndex) this.goTo(index, instant);
  }

  #setActive(index, instant = false) {
    if (index < 0) return;
    this.#activeIndex = index;
    this.#thumbs.forEach((thumb, i) => {
      const active = i === index;
      thumb.toggleAttribute('data-active', active);
      if (active) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    });
    const thumb = this.#thumbs[index];
    if (thumb && this.#strip) {
      const target = thumb.offsetLeft - this.#strip.offsetLeft - (this.#strip.clientWidth - thumb.clientWidth) / 2;
      this.#strip.scrollTo({ left: Math.max(0, target), behavior: this.#behavior(instant) });
    }
  }
}

if (!customElements.get('s-product-gallery')) {
  customElements.define('s-product-gallery', SProductGallery);
}
